import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Copy, Trash2, Link2, Ban, RotateCcw, Check } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

function isExpired(link) {
  if (link.revoked) return true;
  if (!link.expires_at) return false;
  return new Date(link.expires_at).getTime() <= Date.now();
}

function statusOf(link) {
  if (link.revoked) return { label: "Revoked", className: "text-red-600 bg-red-50" };
  if (link.expires_at && isExpired(link)) return { label: "Expired", className: "text-amber-700 bg-amber-50" };
  return { label: "Active", className: "text-emerald-700 bg-emerald-50" };
}

export default function ShareLinks({ API, getAuthHeader }) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const PUBLIC_BASE = window.location.origin;

  const fetchLinks = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/share-links`, getAuthHeader());
      setLinks(data);
    } catch {
      toast.error("Failed to load share links");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLinks(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const buildUrl = (token) => `${PUBLIC_BASE}/access/${token}`;

  const copyLink = async (link) => {
    try {
      await navigator.clipboard.writeText(buildUrl(link.token));
      setCopiedId(link.id);
      toast.success("Link copied");
      setTimeout(() => setCopiedId(null), 1500);
    } catch {
      toast.error("Couldn't copy. Use the URL shown below.");
    }
  };

  const revoke = async (link) => {
    if (!window.confirm("Revoke this link? Existing visitors keep their session, new clicks are blocked.")) return;
    try {
      await axios.patch(`${API}/share-links/${link.id}`, { revoked: true }, getAuthHeader());
      toast.success("Link revoked");
      fetchLinks();
    } catch { toast.error("Failed to revoke link"); }
  };

  const reactivate = async (link) => {
    try {
      await axios.patch(`${API}/share-links/${link.id}`, { revoked: false }, getAuthHeader());
      toast.success("Link reactivated");
      fetchLinks();
    } catch { toast.error("Failed to reactivate link"); }
  };

  const remove = async (link) => {
    if (!window.confirm("Delete this link permanently?")) return;
    try {
      await axios.delete(`${API}/share-links/${link.id}`, getAuthHeader());
      toast.success("Link deleted");
      fetchLinks();
    } catch { toast.error("Failed to delete link"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-[#0A1128]" style={{ fontFamily: "Outfit" }}>
          Share Links
        </h2>
        <Button
          onClick={() => setShowCreate(true)}
          data-testid="create-share-link-btn"
          className="bg-[#FF5A00] hover:bg-[#E04F00] text-white font-bold"
        >
          <Plus className="w-4 h-4 mr-2" /> Generate Link
        </Button>
      </div>

      <p className="text-sm text-[#0A1128]/60 mb-4">
        Pre-authorised private links skip the visitor gate. Revoke or expire any link at any time.
      </p>

      <div className="bg-white rounded-xl border border-[#0A1128]/10 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#EAF0EC]/50">
              <TableHead className="font-bold text-[#0A1128]/70 text-xs uppercase tracking-wider">Label</TableHead>
              <TableHead className="font-bold text-[#0A1128]/70 text-xs uppercase tracking-wider">Status</TableHead>
              <TableHead className="font-bold text-[#0A1128]/70 text-xs uppercase tracking-wider">Created</TableHead>
              <TableHead className="font-bold text-[#0A1128]/70 text-xs uppercase tracking-wider">Expires</TableHead>
              <TableHead className="font-bold text-[#0A1128]/70 text-xs uppercase tracking-wider">Uses</TableHead>
              <TableHead className="font-bold text-[#0A1128]/70 text-xs uppercase tracking-wider">URL</TableHead>
              <TableHead className="font-bold text-[#0A1128]/70 text-xs uppercase tracking-wider text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-[#0A1128]/40">Loading…</TableCell></TableRow>
            ) : links.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-[#0A1128]/40">No share links yet. Generate one above.</TableCell></TableRow>
            ) : links.map((link) => {
              const status = statusOf(link);
              const url = buildUrl(link.token);
              return (
                <TableRow key={link.id} data-testid={`share-link-row-${link.id}`}>
                  <TableCell className="font-medium text-[#0A1128] text-sm">
                    {link.label || <span className="text-[#0A1128]/30 italic">Untitled</span>}
                  </TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${status.className}`}>
                      {status.label}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs text-[#0A1128]/60">{fmtDate(link.created_at)}</TableCell>
                  <TableCell className="text-xs text-[#0A1128]/60">{link.expires_at ? fmtDate(link.expires_at) : <span className="text-[#0A1128]/30">Never</span>}</TableCell>
                  <TableCell className="text-sm text-[#0A1128]/70 font-mono">{link.use_count}</TableCell>
                  <TableCell className="text-xs text-[#0A1128]/50 max-w-[280px] truncate font-mono" title={url}>{url}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center gap-1 justify-end">
                      <Button
                        onClick={() => copyLink(link)}
                        variant="ghost"
                        size="sm"
                        className="text-[#0A1128]/50 hover:text-[#FF5A00]"
                        data-testid={`copy-share-btn-${link.id}`}
                      >
                        {copiedId === link.id ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                      </Button>
                      {link.revoked ? (
                        <Button onClick={() => reactivate(link)} variant="ghost" size="sm" className="text-[#0A1128]/50 hover:text-emerald-600" data-testid={`reactivate-share-btn-${link.id}`}>
                          <RotateCcw className="w-4 h-4" />
                        </Button>
                      ) : (
                        <Button onClick={() => revoke(link)} variant="ghost" size="sm" className="text-[#0A1128]/50 hover:text-amber-600" data-testid={`revoke-share-btn-${link.id}`}>
                          <Ban className="w-4 h-4" />
                        </Button>
                      )}
                      <Button onClick={() => remove(link)} variant="ghost" size="sm" className="text-[#0A1128]/50 hover:text-red-500" data-testid={`delete-share-btn-${link.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <CreateLinkDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        API={API}
        getAuthHeader={getAuthHeader}
        onCreated={(newLink) => { fetchLinks(); toast.success("Link generated"); copyLink(newLink); }}
      />
    </div>
  );
}

function CreateLinkDialog({ open, onClose, API, getAuthHeader, onCreated }) {
  const [label, setLabel] = useState("");
  const [expiryMode, setExpiryMode] = useState("never"); // never | preset | custom
  const [presetDays, setPresetDays] = useState(7);
  const [customAt, setCustomAt] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setLabel("");
    setExpiryMode("never");
    setPresetDays(7);
    setCustomAt("");
  };

  const handleClose = () => { reset(); onClose(); };

  const submit = async (e) => {
    e.preventDefault();
    if (!label.trim()) {
      toast.error("Please add a label so you remember who it's for");
      return;
    }
    let expires_at = null;
    if (expiryMode === "preset") {
      expires_at = new Date(Date.now() + presetDays * 24 * 3600 * 1000).toISOString();
    } else if (expiryMode === "custom") {
      if (!customAt) { toast.error("Pick an expiry date"); return; }
      const dt = new Date(customAt);
      if (Number.isNaN(dt.getTime())) { toast.error("Invalid date"); return; }
      if (dt.getTime() <= Date.now()) { toast.error("Expiry must be in the future"); return; }
      expires_at = dt.toISOString();
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/share-links`, { label: label.trim(), expires_at }, getAuthHeader());
      onCreated(data);
      handleClose();
    } catch {
      toast.error("Failed to create share link");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-md" data-testid="create-share-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <Link2 className="w-5 h-5 text-[#FF5A00]" /> Generate Share Link
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="space-y-2">
            <Label className="text-xs font-bold text-[#0A1128]/60 uppercase tracking-wider">Label *</Label>
            <Input
              data-testid="share-label-input"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder='e.g. "Acme demo - Sarah"'
              className="h-10 bg-[#EAF0EC]/50 border-[#0A1128]/10"
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-bold text-[#0A1128]/60 uppercase tracking-wider">Expiry</Label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { id: "never", label: "Never" },
                { id: "preset", label: "In N days" },
                { id: "custom", label: "Specific" },
              ].map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setExpiryMode(m.id)}
                  data-testid={`expiry-mode-${m.id}`}
                  className={`text-xs font-bold uppercase tracking-wider rounded-lg py-2 transition-all ${
                    expiryMode === m.id
                      ? "bg-[#FF5A00] text-white"
                      : "bg-[#EAF0EC]/50 text-[#0A1128]/70 hover:bg-[#0A1128]/5"
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
            {expiryMode === "preset" && (
              <Input
                type="number"
                min={1}
                value={presetDays}
                onChange={(e) => setPresetDays(Math.max(1, parseInt(e.target.value || "1", 10)))}
                className="h-10 bg-[#EAF0EC]/50 border-[#0A1128]/10"
                data-testid="share-preset-days"
              />
            )}
            {expiryMode === "custom" && (
              <Input
                type="datetime-local"
                value={customAt}
                onChange={(e) => setCustomAt(e.target.value)}
                className="h-10 bg-[#EAF0EC]/50 border-[#0A1128]/10"
                data-testid="share-custom-datetime"
              />
            )}
          </div>

          <div className="flex gap-2 pt-2">
            <Button type="submit" disabled={submitting} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white font-bold flex-1" data-testid="share-submit-btn">
              {submitting ? "Generating…" : "Generate"}
            </Button>
            <Button type="button" variant="outline" onClick={handleClose}>Cancel</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
